import { useEffect, useRef } from "react";
import { getStoredToken, postSessionRefresh } from "../helpers/api/apiCore";

/**
 * Global session keeper — same behavior as Facebook / Instagram / WhatsApp
 * when you switch tabs and come back.
 *
 * Problème résolu : Chrome/Safari suspendent complètement un onglet
 * backgroundé plus de ~5 min. Le setInterval de rafraîchissement est gelé,
 * le token access expire, et au retour l'UI se retrouve avec des requêtes
 * qui échouent en 401 sans que le token soit rafraîchi de manière fiable.
 *
 * Ce composant fait 2 choses :
 *   1. Au retour sur l'onglet après > SESSION_STALE_MS (30s), appelle
 *      explicitement POST /sessions/refresh. Le nouveau access token est
 *      stocké par l'interceptor existant.
 *   2. Broadcast un événement global 'moga:session-refreshed' sur succès →
 *      les pages qui l'écoutent (Orders, Shippings, DeliveryFees…) refetch
 *      leur data.
 *   3. En cas de 401/403 sur le refresh (session révoquée serveur-side),
 *      redirect vers login proprement — pas de spin infini.
 */

const SESSION_STALE_MS = 30 * 1000; // 30 secondes en background = déclenche refresh
const REFRESH_EVENT = "moga:session-refreshed";

const SessionKeeper: React.FC = () => {
  const hiddenAtRef = useRef<number | null>(null);
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const doRefresh = async () => {
      if (refreshingRef.current) return;
      if (!getStoredToken()) return; // pas connecté → rien à faire
      refreshingRef.current = true;
      try {
        await postSessionRefresh();
        // L'interceptor a déjà mis à jour le token en localStorage.
        // Broadcast pour que les pages refetch leurs données stales.
        window.dispatchEvent(new Event(REFRESH_EVENT));
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Session vraiment révoquée serveur-side. L'interceptor a déjà
          // fait le cleanup + redirect, on ne double pas.
          if (process.env.NODE_ENV === "development") {
            console.warn("[SessionKeeper] session revoked", status);
          }
        } else {
          // Erreur réseau transitoire → on garde la session, l'utilisateur
          // ré-essaiera à la prochaine action.
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[SessionKeeper] transient refresh error, keeping session",
              err?.code || err?.message
            );
          }
        }
      } finally {
        refreshingRef.current = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }
      // visible
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!hiddenAt) return;
      const away = Date.now() - hiddenAt;
      if (away >= SESSION_STALE_MS) {
        if (process.env.NODE_ENV === "development") {
          console.log(`[SessionKeeper] tab visible after ${Math.round(away / 1000)}s → refresh + refetch`);
        }
        doRefresh();
      }
    };

    // Cas 2 : la fenêtre reprend le focus après avoir été minimisée sur
    // le desktop (visibilitychange ne fire pas toujours dans ce cas).
    const onFocus = () => {
      if (document.visibilityState === "visible" && hiddenAtRef.current) {
        onVisibility();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return null;
};

export default SessionKeeper;
